import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeftIcon, 
  PlayIcon, 
  CheckIcon,
  StarIcon,
  ShoppingCartIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { Product } from '../data/products';
import PurchaseModal from './PurchaseModal';
import PackageSizeModal from './PackageSizeModal';

interface ProductPageProps {
  product: Product;
  onBack: () => void;
}

const ProductPage: React.FC<ProductPageProps> = ({ product, onBack }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [isPackageSizeModalOpen, setIsPackageSizeModalOpen] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'specifications', label: 'Specifications' },
    { id: 'instructions', label: 'Instructions' },
    { id: 'media', label: 'Media' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-primary-600 transition-colors mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Products
          </button>
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{product.title}</h1>
              <p className="text-lg text-gray-600 mt-2">{product.subtitle}</p>
            </div>
            
            <div className="flex items-center gap-4 mt-4 lg:mt-0">
              <div className="flex items-center">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <span className="ml-2 text-sm text-gray-600">(4.8/5)</span>
              </div>
              
              <div className="text-right">
                <div className="text-2xl font-bold text-primary-600">{product.price}</div>
                <div className="text-sm text-green-600">{product.availability}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="aspect-video bg-white rounded-lg shadow-lg overflow-hidden">
              <img
                src={product.images[selectedImage]?.src || '/images/placeholder.jpg'}
                alt={product.images[selectedImage]?.alt || 'Product image'}
                className="w-full h-full object-contain"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`aspect-video rounded-lg overflow-hidden border-2 transition-colors ${
                    selectedImage === index 
                      ? 'border-primary-500' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-contain"
                  />
                </button>
              ))}
            </div>
            
            {product.images[selectedImage]?.caption && (
              <p className="text-sm text-gray-600 text-center">
                {product.images[selectedImage].caption}
              </p>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Description</h2>
              <p className="text-gray-600 leading-relaxed">{product.description}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
              <ul className="space-y-2">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex gap-4">
              <button 
                onClick={() => {
                  if (product.id === 'packaging-coverage') {
                    // Show package size selection modal for packaging product
                    setIsPackageSizeModalOpen(true);
                  } else if (product.stripeCheckoutUrl) {
                    // Open Stripe checkout in new tab for products with direct URL
                    window.open(product.stripeCheckoutUrl, '_blank');
                  } else {
                    // Open purchase modal for other products
                    setIsPurchaseModalOpen(true);
                  }
                }}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                <ShoppingCartIcon className="w-5 h-5" />
                Buy Now
              </button>
              <button 
                onClick={() => {
                  // Save product to localStorage cache
                  try {
                    const savedProducts = JSON.parse(localStorage.getItem('savedProducts') || '[]');
                    const isAlreadySaved = savedProducts.some((p: Product) => p.id === product.id);
                    
                    if (isAlreadySaved) {
                      // Remove from saved if already saved
                      const updatedProducts = savedProducts.filter((p: Product) => p.id !== product.id);
                      localStorage.setItem('savedProducts', JSON.stringify(updatedProducts));
                      alert('Product removed from saved items');
                    } else {
                      // Add to saved
                      savedProducts.push({
                        id: product.id,
                        title: product.title,
                        subtitle: product.subtitle
                      });
                      localStorage.setItem('savedProducts', JSON.stringify(savedProducts));
                      alert('Product saved to favorites!');
                    }
                  } catch (error) {
                    console.error('Error saving product:', error);
                    alert('Error saving product. Please try again.');
                  }
                }}
                className="btn-secondary flex items-center justify-center gap-2 px-6"
              >
                <HeartIcon className="w-5 h-5" />
                Save
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-12">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="mt-8">
            {activeTab === 'overview' && (
              <div className="prose max-w-none">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Product Overview</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{product.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Features</h4>
                    <ul className="space-y-2">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Related Products</h4>
                    <ul className="space-y-2">
                      {product.relatedProducts.map((related, index) => (
                        <li key={index}>
                          <a
                            href={`#${related}`}
                            className="text-primary-600 hover:text-primary-700 transition-colors"
                          >
                            {related.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'specifications' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Specifications</h3>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <tbody className="bg-white divide-y divide-gray-200">
                      {product.specifications.map((spec, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {spec.label}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {spec.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'instructions' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Step-by-Step Instructions</h3>
                <div className="space-y-6">
                  {product.instructions.map((instruction, index) => (
                    <motion.div
                      key={index}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                          <span className="text-primary-600 font-semibold text-sm">
                            {instruction.step}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 mb-2">
                            {instruction.title}
                          </h4>
                          <p className="text-gray-600 mb-3">{instruction.description}</p>
                          {instruction.tips && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <p className="text-blue-800 text-sm">
                                <strong>Pro Tip:</strong> {instruction.tips}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'media' && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Media & Resources</h3>
                
                {product.videos && product.videos.length > 0 && (
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Instructional Videos</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {product.videos.map((video, index) => (
                        <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                          <div className="aspect-video bg-gray-100 flex items-center justify-center">
                            <div className="text-center">
                              <PlayIcon className="w-16 h-16 text-primary-600 mx-auto mb-2" />
                              <p className="text-gray-600">{video.title}</p>
                            </div>
                          </div>
                          <div className="p-4">
                            <h5 className="font-semibold text-gray-900 mb-2">{video.title}</h5>
                            <p className="text-gray-600 text-sm">{video.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Product Images</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {product.images.map((image, index) => (
                      <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="aspect-video">
                          <img
                            src={image.src}
                            alt={image.alt}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        {image.caption && (
                          <div className="p-3">
                            <p className="text-sm text-gray-600">{image.caption}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      <PurchaseModal 
        isOpen={isPurchaseModalOpen} 
        onClose={() => setIsPurchaseModalOpen(false)} 
      />

      {/* Package Size Selection Modal */}
      <PackageSizeModal 
        isOpen={isPackageSizeModalOpen} 
        onClose={() => setIsPackageSizeModalOpen(false)} 
      />
    </div>
  );
};

export default ProductPage;
